import { z } from 'zod';
import { dataSchema, uuidSchema } from './comum.ts';
import {
  fatiaDaFaixaSchema,
  serieDeValoresSchema,
  totalDeTitulosSchema,
} from './painel-titulos.ts';

/**
 * Painel de contas a pagar — o que se deve, para quando, e a quem
 * (`.spec/010`, módulo Pagamento; `.spec/016`, seção 10).
 *
 * É **só leitura e só agregação**, como o painel de compras: nenhum título nasce,
 * muda ou some por aqui. O painel conta o que a nota, o contrato e o lançamento
 * manual gravaram, e o que as baixas abateram.
 *
 * **As faixas, a janela e o corte dos gráficos moram em `painel-titulos.ts`**, e
 * não aqui: o painel de contas a receber usa exatamente os mesmos, porque uma
 * dívida e um crédito que vencem em nove dias caem na mesma faixa e ela se
 * calcula da mesma forma. O que sobrou neste arquivo é o que só vale para o
 * lado que paga.
 *
 * ## O que ele mede é o **saldo**, não o valor
 *
 * Todo número de "em aberto" desta tela sai de `valor_saldo`, e não de `valor`.
 * A diferença aparece no título baixado em parte: um de R$ 1.000,00 com
 * R$ 700,00 pagos deve R$ 300,00, e é R$ 300,00 que precisa entrar no fluxo de
 * caixa da semana. Somar o valor original faria a previsão de desembolso incluir
 * dinheiro que já saiu — e o erro é sempre para mais, que é o pior lado.
 *
 * É a mesma verdade que `situacaoDoTitulo` lê: o título não tem coluna de
 * situação, o saldo é quem responde.
 *
 * ## Duas janelas, como no painel de compras
 *
 * - **As faixas de vencimento e os dois gráficos de "em aberto"** olham para a
 *   frente: são o que ainda se deve, seja qual for a data em que o título
 *   nasceu. O título de janeiro ainda em aberto em agosto é justamente o que
 *   precisa aparecer.
 * - **Os dois gráficos de "pagas"** olham para trás, 30 dias: são o que saiu do
 *   caixa. Sem recorte, virariam o histórico inteiro da conta, em que o ano
 *   passado esmaga o mês atual.
 *
 * ## Por que a faixa "depois de 90 dias" existe, se ninguém a pediu
 *
 * Sem ela, as seis faixas somariam menos que o indicador "em aberto" e nada na
 * tela explicaria a diferença. Números que não fecham são o defeito que gera
 * chamado: quem confere soma as barras, não bate com o total, e conclui que o
 * sistema perdeu um título. Com a sétima faixa, **atraso + as cinco + depois de
 * 90 = em aberto**, exatamente, e a tela pode afirmar isso.
 */

/**
 * O recorte do painel — os mesmos dois do painel de compras.
 *
 * Escrito aqui, e não em `painel-titulos.ts` junto do resto que os dois painéis
 * compartilham, ainda que hoje seja idêntico ao do receber: a consulta é a
 * fronteira pública desta rota, e o dia em que um dos dois ganhar um filtro que
 * o outro não tem — fornecedor aqui, cliente lá — a divisão já está feita. São
 * quatro linhas de Zod; um schema compartilhado economizaria isso e cobraria uma
 * separação urgente no primeiro filtro assimétrico.
 *
 * Ausentes significam "tudo que o usuário alcança": o recorte por empresa
 * vinculada continua sendo do servidor, como em toda listagem.
 *
 * **A obra alcança o título pelo rateio**, que é o único caminho que existe. O
 * título não tem obra no cabeçalho — a nota que o gerou trouxe cimento para três
 * canteiros —, e quem sabe para onde o custo vai é `contas_pagar_rateio`. A
 * consequência tem de ser dita na tela: com uma obra escolhida, **o título sem
 * rateio fica de fora**. Ele é a compra de almoxarifado central, que não se
 * divide, e atribuí-lo a todas as obras faria o mesmo dinheiro aparecer em cada
 * recorte.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const painelContasPagarQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    obraId: uuidSchema.optional(),
  })
  .strict();

export type PainelContasPagarQuery = z.infer<typeof painelContasPagarQuerySchema>;

export const painelContasPagarSchema = z.object({
  /** Hoje pelo relógio do servidor — o corte entre o atraso e o resto. */
  hoje: dataSchema,
  /**
   * Tudo que tem saldo, **sem recorte de data**.
   *
   * É a soma exata das sete faixas — o servidor a calcula com `totalDasFaixas`,
   * sobre as mesmas linhas, em vez de perguntar de novo ao banco. Por
   * construção, então, o total fecha com os indicadores, e a tela pode afirmar
   * isso ao usuário. Uma consulta própria varreria as mesmas linhas para chegar
   * ao mesmo número, com a chance de chegar a um diferente.
   */
  emAberto: totalDeTitulosSchema,
  /** As sete faixas, na ordem do tempo, completas. */
  porFaixa: z.array(fatiaDaFaixaSchema),
  /** Primeiro dia da janela das pagas. */
  pagasDe: dataSchema,
  /** Último dia da janela das pagas — hoje. */
  pagasAte: dataSchema,
  /** As baixas não estornadas da janela: quantas e quanto de principal. */
  pagas: totalDeTitulosSchema,
  emAbertoPorContaContabil: serieDeValoresSchema,
  emAbertoPorFornecedor: serieDeValoresSchema,
  pagasPorContaContabil: serieDeValoresSchema,
  pagasPorFornecedor: serieDeValoresSchema,
});

export type PainelContasPagar = z.infer<typeof painelContasPagarSchema>;
